/**
 * Days-to-expiry, turned into something an operator can see at a glance.
 *
 * This is the most consequential logic in the dashboard. A claim filed one day
 * past its window is worth exactly zero and cannot be appealed, so the queue's
 * ordering and its urgency colours are not presentation — they are the control
 * that stops money being lost to a clock nobody was watching.
 *
 * The tiers deliberately mirror `ALERT_TIERS` in `apps/web/src/jobs/expiry.ts`.
 * An operator gets a 21-day email and then opens the console; if the console
 * disagreed about what "urgent" means, the email would be teaching them to
 * distrust the screen. If those tiers change, change them in both places — a
 * test pins the values here so the drift is at least visible.
 */

export type UrgencyTier =
  /** Past the deadline. Unfileable, unappealable, counted as a miss. */
  | 'lapsed'
  /** 0–7 days. Phase 6 calls for this to be a red banner in the console. */
  | 'critical'
  /** 8–14 days. */
  | 'high'
  /** 15–21 days. Roughly when chasing a freight forwarder has to start. */
  | 'medium'
  /** More than 21 days out. */
  | 'low'
  /** No deadline at all — the two settlement classes. */
  | 'none';

/**
 * Upper bounds, in days remaining, inclusive.
 *
 * Read as: `daysLeft <= critical` is critical. Zero is the last filing day and
 * therefore still critical rather than lapsed.
 */
export const URGENCY_THRESHOLDS = {
  critical: 7,
  high: 14,
  medium: 21,
} as const;

export function urgencyTier(daysLeft: number | null): UrgencyTier {
  if (daysLeft === null) return 'none';
  if (daysLeft < 0) return 'lapsed';
  if (daysLeft <= URGENCY_THRESHOLDS.critical) return 'critical';
  if (daysLeft <= URGENCY_THRESHOLDS.high) return 'high';
  if (daysLeft <= URGENCY_THRESHOLDS.medium) return 'medium';
  return 'low';
}

/** Ranked worst-first, so a tier can be compared without a lookup table at the call site. */
const TIER_RANK: Record<UrgencyTier, number> = {
  lapsed: 0,
  critical: 1,
  high: 2,
  medium: 3,
  low: 4,
  none: 5,
};

export function isMoreUrgent(a: UrgencyTier, b: UrgencyTier): boolean {
  return TIER_RANK[a] < TIER_RANK[b];
}

/**
 * The number an operator scans for. Kept short — the row already says "left".
 *
 * Colour alone must never carry the tier (an operator may be colour-blind, and
 * the console is read under time pressure), so this text is the second signal
 * and it states the case in words.
 */
export function urgencyText(daysLeft: number | null): string {
  if (daysLeft === null) return 'No deadline';
  if (daysLeft < 0) {
    const overdue = Math.abs(daysLeft);
    return `Lapsed ${overdue} ${overdue === 1 ? 'day' : 'days'} ago`;
  }
  if (daysLeft === 0) return 'Last day to file';
  return `${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} left`;
}

/** The big glyph in the queue's first column. Compact by design; `urgencyText` carries the words. */
export function urgencyGlyph(daysLeft: number | null): string {
  if (daysLeft === null) return '—';
  if (daysLeft < 0) return '!';
  return String(daysLeft);
}

/**
 * Days remaining, computed from two ISO calendar days.
 *
 * The ops queue receives `daysLeft` already computed by the server; the seller
 * endpoint sends only `expiresAt` and the `asOf` day it was generated against,
 * so this closes that gap. Both dates are UTC calendar days and are subtracted
 * as such — parsing them into local `Date`s would shift the result by one for
 * anyone west of Greenwich, and one day is the entire difference between a
 * claim that can be filed and one that cannot.
 *
 * Zero means today is the last day. Negative means the window has closed.
 */
export function daysUntil(expiresAt: string | null, asOf: string): number | null {
  if (!expiresAt || !asOf) return null;
  const end = Date.parse(`${expiresAt.slice(0, 10)}T00:00:00.000Z`);
  const start = Date.parse(`${asOf.slice(0, 10)}T00:00:00.000Z`);
  if (Number.isNaN(end) || Number.isNaN(start)) return null;
  return Math.round((end - start) / 86_400_000);
}

export interface Expiring {
  readonly daysLeft: number | null;
  readonly valueMinor: number | null;
}

/**
 * Queue order: soonest deadline first, no-deadline last, ties by value.
 *
 * The server already sorts this way. Re-establishing it here is not redundancy
 * for its own sake — it means a change to the SQL `ORDER BY`, a client-side
 * filter, or a merge of two responses cannot silently un-sort the one screen
 * where ordering is what prevents a claim from lapsing unnoticed.
 *
 * Nulls last matters specifically: the settlement classes have no window, and
 * a few hundred of them at the top would bury everything that expires this
 * week.
 */
export function sortByUrgency<T extends Expiring>(rows: readonly T[]): T[] {
  return [...rows].sort((a, b) => {
    if (a.daysLeft === null && b.daysLeft === null) return byValueDesc(a, b);
    if (a.daysLeft === null) return 1;
    if (b.daysLeft === null) return -1;
    if (a.daysLeft !== b.daysLeft) return a.daysLeft - b.daysLeft;
    return byValueDesc(a, b);
  });
}

/** Bigger money first within a day, matching the server's `desc(valueMinor)` tie-break. */
function byValueDesc(a: Expiring, b: Expiring): number {
  const left = a.valueMinor ?? -1;
  const right = b.valueMinor ?? -1;
  return right - left;
}

/** How many findings sit in each tier. Drives the queue's summary strip and its filter chips. */
export function countByTier(rows: readonly Expiring[]): Record<UrgencyTier, number> {
  const counts: Record<UrgencyTier, number> = {
    lapsed: 0,
    critical: 0,
    high: 0,
    medium: 0,
    low: 0,
    none: 0,
  };
  for (const row of rows) counts[urgencyTier(row.daysLeft)] += 1;
  return counts;
}
